
import { Component, Vue } from 'vue-property-decorator';
import TicketRepositoryInterface, {
  TicketInterface, TicketPriorities,
  TicketWSMessageInterface,
} from '@/services/QA/ticket/interfaces';
import TagRepositoryInterface, { TagInterface } from '@/services/QA/tag/interfaces';
import ScoreTypeRepositoryInterface, { ScoreTypeInterface } from '@/services/QA/scoreType/interfaces';
import ScoreGradeRepositoryInterface, { ScoreGradeInterface } from '@/services/QA/scoreGrade/interfaces';
import CommentRepositoryInterface, { CommentInterface } from '@/services/QA/comment/interfaces';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import NoteRepositoryInterface, { NoteInterface } from '@/services/QA/note/interfaces';
import EscalateDialog from '@/components/dialogs/qa/escalation/EscalateDialog.vue';
import FeedbackDialog from '@/components/dialogs/qa/feedback/FeedbackDialog.vue';
import DeleteCommentDialog from '@/components/dialogs/qa/comment/DeleteCommentDialog.vue';
import DeleteNoteDialog from '@/components/dialogs/qa/note/DeleteNoteDialog.vue';
import EditCommentDialog from '@/components/dialogs/qa/comment/EditCommentDialog.vue';
import EditNoteDialog from '@/components/dialogs/qa/note/EditNoteDialog.vue';
import { UserHasScopes } from '@/views/middleware/Route/Auth';
import { Roles } from '@/services/Roles';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';

@Component({
  inject: [
    'tagRepository',
    'userRepository',
    'noteRepository',
    'ticketRepository',
    'commentRepository',
    'scoreTypeRepository',
    'scoreGradeRepository',
    'profileRepository',
  ],
  components: {
    EscalateDialog,
    FeedbackDialog,
    DeleteCommentDialog,
    DeleteNoteDialog,
    EditCommentDialog,
    EditNoteDialog,
  },
  sockets: {
    ticketData(msg: TicketWSMessageInterface): void {
      this.$emit('ticketUpdate', msg.ticket);
    },
  },
})
export default class ReviewTicket extends Vue {
  private ticketRepository!: TicketRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private scoreTypeRepository!: ScoreTypeRepositoryInterface;
  private scoreGradeRepository!: ScoreGradeRepositoryInterface;
  private tagRepository!: TagRepositoryInterface;
  private commentRepository!: CommentRepositoryInterface;
  private noteRepository!: NoteRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private ticket: TicketInterface = {} as TicketInterface;
  private messageReceived: boolean = false;
  private responseReceived: boolean = false;
  private loading: boolean = true;
  private comment: string = '';
  private replyComment: string = '';
  private privateNote: string = '';
  private valid: boolean = false;
  private searchTag: string = '';
  private tags: TagInterface[] = [];
  private tagsSelected: TagInterface[] = [];
  private scoreTypes: ScoreTypeInterface[] = [];
  private scoreGrades: ScoreGradeInterface[] = [];
  private scores: { [scoreTypeId: number]: number } = {};
  private usersMap: { [userId: string]: UserBaseInfoInterface } = {};
  private escalateDialog: string | false = false;
  private feedbackDialog: string | false = false;
  private userHasScopes: object = UserHasScopes;
  private userRoles: object = Roles;
  private commentIdForDialog: number = 0;
  private deleteCommentDialog: string | false = false;
  private noteIdForDialog: number = 0;
  private deleteNoteDialog: string | false = false;
  private commentIdForEditDialog: number = 0;
  private commentContentForDialog: string = '';
  private editCommentDialog: string | false = false;
  private noteIdForEditDialog: number = 0;
  private noteContentForDialog: string = '';
  private editNoteDialog: string | false = false;
  private ticketPriorities: Array<string | TicketPriorities>;


  public beforeCreate(): void {
    this.ticketPriorities = Object.values(TicketPriorities);
    this.$on('ticketUpdate', async (ticket: TicketInterface) => {
      await this.getUsersInfoByEmails(ticket);
      this.messageReceived = true;
      this.mergeTicketProperties(ticket);
    });
  }

  public async created(): Promise<void> {
    try {
      this.tags = await this.tagRepository.getActiveTags();
      this.scoreTypes = await this.scoreTypeRepository.getAllScoreTypes();
      this.scoreGrades = await this.scoreGradeRepository.getAllScoreGrades();
      const ticketId = Number(this.$route.params.ticketId);
      const ticket = await this.ticketRepository.getUnreviewedTicket(ticketId);
      ticket.replies.map((reply) => {
        reply.commentInputVisible = false;
        return reply;
      });
      await this.getUsersInfoByIds(ticket);
      this.ticket = ticket;
      this.responseReceived = true;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message);
      await this.$router.push({ path: '/qc/tickets' });
    }
  }

  public mergeTicketProperties(ticketData: TicketInterface): void {
    if (!this.responseReceived) {
      setTimeout(() => this.mergeTicketProperties(ticketData), 200);
      return;
    }

    if (ticketData.id !== this.ticket.id) {
      this.messageReceived = false;
      return;
    }

    Object.getOwnPropertyNames(ticketData).forEach((property: string) => {
      if (!this.ticket[property]) {
        this.ticket[property] = ticketData[property];
      } else if (Array.isArray(ticketData[property])) {
        (ticketData[property] as []).forEach((ticketDataArrItem: { id: number }) => {
          const index = (this.ticket[property] as [])
            .findIndex((value: { id: number }) => value.id === ticketDataArrItem.id);
          if (index !== -1) {
            Object.assign((this.ticket[property] as [])[index], ticketDataArrItem);
          } else {
            (this.ticket[property] as unknown[]).push(ticketDataArrItem);
          }
        });
      }
    });

    this.ticket.replies = this.ticket.replies.sort((replyA, replyB) => replyA.date.localeCompare(replyB.date));
    this.loading = !(this.responseReceived && this.messageReceived);
    this.$forceUpdate();
  }

  private async addComment(): Promise<void> {
    try {
      const commentDTO: Partial<CommentInterface> = {
        content: this.comment,
        authorId: this.$store.getters.id,
        timestamp: Math.floor(Date.now() / 1000),
      };

      const comment = await this.commentRepository.addTicketComment(this.ticket.id, commentDTO);
      if (!this.ticket.comments) {
        this.ticket.comments = [];
      }

      if (!this.usersMap[this.$store.getters.id]) {
        this.usersMap[this.$store.getters.id] = {
          id: this.$store.getters.id,
          email: this.$store.getters.email,
        };
      }

      this.ticket.comments.push(comment);
      this.comment = '';

    } catch (err) {
      console.log(err);
      this.$errorDialog.show(err.response?.data?.message);
    }
  }

  private async addCommentToReply(replyId: number): Promise<void> {
    try {
      const commentDTO: Partial<CommentInterface> = {
        content: this.replyComment,
        authorId: this.$store.getters.id,
        timestamp: Math.floor(Date.now() / 1000),
      };

      const comment = await this.commentRepository.addReplyComment(this.ticket.id, replyId, commentDTO);

      if (!this.usersMap[this.$store.getters.id]) {
        this.usersMap[this.$store.getters.id] = {
          id: this.$store.getters.id,
          email: this.$store.getters.email,
        };
      }

      const index = this.ticket.replies.findIndex((reply) => reply.id === replyId);
      this.ticket.replies[index].comments.push(comment);
      this.replyComment = '';

    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message);
    }
  }

  private async addNote(): Promise<void> {
    try {
      const noteDTO: Partial<NoteInterface> = {
        content: this.privateNote,
        authorId: this.$store.getters.id,
        timestamp: Math.floor(Date.now() / 1000),
      };

      const note = await this.noteRepository.addTicketNote(this.ticket.id, noteDTO);

      if (!this.usersMap[this.$store.getters.id]) {
        this.usersMap[this.$store.getters.id] = {
          id: this.$store.getters.id,
          email: this.$store.getters.email,
        };
      }

      this.ticket.notes.push(note);
      this.privateNote = '';

    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message);
    }
  }

  private async reviewTicket(sendStats: boolean = true): Promise<void> {
    try {
      await this.ticketRepository.reviewTicket(
        this.ticket.id,
        this.ticket.agents.map((agent) => agent.id),
        this.scores,
        this.tagsSelected.map((tag) => tag.id),
        sendStats,
      );

      if (sendStats) {
        this.$successDialog.show('Ticket Reviewed Successfully!');
      }

      await this.$router.push({ path: '/qc/tickets' });
    } catch (err) {
      console.log(err);
      this.$errorDialog.show(err.response?.data?.message);
    }
  }

  private async escalate(): Promise<void> {
    this.escalateDialog = 'escalate-dialog';
  }

  private async feedback(): Promise<void> {
    this.feedbackDialog = 'feedback-dialog';
  }

  private async createTag(): Promise<void> {
    if (!this.searchTag) {
      return;
    }

    const newTag = await this.tagRepository.createTag({
      label: this.searchTag,
      color: '#808080FF',
    });

    this.tags.push(newTag);
    // @ts-ignore
    this.tagsSelected.push(newTag);
  }

  private addScore(scoreTypeId: number, scoreGradeId: number): void {
    this.scores[scoreTypeId] = scoreGradeId;
    this.valid = Object.keys(this.scores).length === this.scoreTypes.length;
  }

  private removeScore(scoreTypeId: number): void {
    delete this.scores[scoreTypeId];
    this.valid = Object.keys(this.scores).length === this.scoreTypes.length;
  }

  private showCommentArea(replyId: number): void {
    const index = this.ticket.replies.findIndex((reply) => reply.id === replyId);
    this.ticket.replies.forEach((reply, i) => {
      if (i !== index) {
        reply.commentInputVisible = false;
      }
    });
    this.ticket.replies[index].commentInputVisible = !this.ticket.replies[index].commentInputVisible;
    this.$forceUpdate();
  }

  private async getUsersInfoByIds(ticket: TicketInterface): Promise<void> {
    const usersIds: string[] = [];

    ticket.comments.forEach((ticketComment) => {
      if (usersIds.indexOf(ticketComment.comment.authorId) === -1) {
        usersIds.push(ticketComment.comment.authorId);
      }
    });

    ticket.notes.forEach((ticketNote) => {
      if (usersIds.indexOf(ticketNote.note.authorId) === -1) {
        usersIds.push(ticketNote.note.authorId);
      }
    });

    ticket.replies.forEach((reply) => {
      if (!reply.comments.length) {
        return;
      }

      reply.comments.forEach((comment) => {
        if (usersIds.indexOf(comment.comment.authorId) === -1) {
          usersIds.push(comment.comment.authorId);
        }
      });
    });

    const users = await this.userRepository.getUsersByIds(usersIds);
    users.forEach((user) => {
      this.usersMap[user.id] = user;
    });
  }

  private async getUsersInfoByEmails(ticket: TicketInterface): Promise<void> {
    ticket.agents = ticket.agents.filter((agent) => !!agent.email);
    const usersEmails: string[] = [];
    ticket.agents.forEach((agent) => {
      if (usersEmails.indexOf(agent.email) === -1) {
        usersEmails.push(agent.email);
      }
    });

    const users = await this.userRepository.getUsersByEmails(usersEmails);

    const missingUsers = usersEmails.filter((userEmail) => !users.map((user) => user.email).includes(userEmail));
    if (missingUsers.length) {
      this.$errorDialog.show(
        `There Are Some Missing Users (${missingUsers.join(', ')})... Please register them before review the ticket`,
      );
      await this.$router.back();
      return;
    }

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(
      users.map((user) => user.id),
    );

    users.forEach((user) => {
      const profileIndex = profiles.findIndex((profile) => profile.userId === String(user.id));

      if (profileIndex === -1) {
        return;
      }

      this.usersMap[user.id] = user;
      const index = ticket.agents.findIndex((agent) => agent.email === user.email);
      if (index !== -1) {
        ticket.agents[index].id = user.id;
        ticket.agents[index].fullName =
          `${profiles[profileIndex].firstName} ${profiles[profileIndex].lastName}`;
      }
    });
  }

  private async closeReview(): Promise<void> {
    await this.$router.push({ path: '/qc/tickets' });
  }

  private async prepareCommentForEdit(commentId: number): Promise<void> {
    let commentIndex = this.ticket.comments.findIndex((ticketComment) => ticketComment.comment.id === commentId);
    if (commentIndex !== -1) {
      this.commentIdForEditDialog = this.ticket.comments[commentIndex].comment.id;
      this.commentContentForDialog = this.ticket.comments[commentIndex].comment.content;
      this.editCommentDialog = 'edit-comment-dialog';
    } else {
      let replyIndex = -1;

      this.ticket.replies.forEach((reply, mi) => {
        reply.comments.forEach((replyComment, ci) => {
          if (replyComment.comment.id === commentId) {
            replyIndex = mi;
            commentIndex = ci;
          }
        });
      });

      this.commentIdForEditDialog = this.ticket.replies[replyIndex].comments[commentIndex].comment.id;
      this.commentContentForDialog = this.ticket.replies[replyIndex].comments[commentIndex].comment.content;
      this.editCommentDialog = 'edit-comment-dialog';
    }
  }

  private async editComment(commentId: number, content: string): Promise<void> {
    let commentIndex = this.ticket.comments.findIndex((ticketComment) => ticketComment.comment.id === commentId);
    if (commentIndex !== -1) {
      this.ticket.comments[commentIndex].comment.content = content;
      this.ticket.comments[commentIndex].comment.updatedAt = Math.floor(Date.now() / 1000);
    } else {
      let replyIndex = -1;

      this.ticket.replies.forEach((reply, mi) => {
        reply.comments.forEach((replyComment, ci) => {
          if (replyComment.comment.id === commentId) {
            replyIndex = mi;
            commentIndex = ci;
          }
        });
      });

      this.ticket.replies[replyIndex].comments[commentIndex].comment.content = content;
      this.ticket.replies[replyIndex].comments[commentIndex].comment.updatedAt = Math.floor(Date.now() / 1000);
    }
  }

  private async deleteComment(commentId: number): Promise<void> {
    this.commentIdForDialog = commentId;
    this.deleteCommentDialog = 'delete-comment-dialog';
  }

  private async prepareNoteForEdit(noteId: number): Promise<void> {
    const noteIndex = this.ticket.notes.findIndex((ticketNote) => ticketNote.note.id === noteId);
    if (noteIndex !== -1) {
      this.noteIdForEditDialog = this.ticket.notes[noteIndex].note.id;
      this.noteContentForDialog = this.ticket.notes[noteIndex].note.content;
      this.editNoteDialog = 'edit-note-dialog';
    }
  }

  private async editNote(noteId: number, content: string): Promise<void> {
    const noteIndex = this.ticket.notes.findIndex((ticketNote) => ticketNote.note.id === noteId);
    if (noteIndex !== -1) {
      this.ticket.notes[noteIndex].note.content = content;
      this.ticket.notes[noteIndex].note.updatedAt = Math.floor(Date.now() / 1000);
    }
  }

  private async deleteNote(noteId: number): Promise<void> {
    this.noteIdForDialog = noteId;
    this.deleteNoteDialog = 'delete-note-dialog';
  }

  private removeNote(noteId: number): void {
    const noteIndex = this.ticket.notes.findIndex((ticketNote) => ticketNote.note.id === noteId);

    if (noteIndex !== -1) {
      delete this.ticket.notes[noteIndex];
      this.ticket.notes = this.ticket.notes.filter((note) => !!note);
    }

    this.$forceUpdate();
  }

  private removeComment(commentId: number): void {
    let commentIndex = this.ticket.comments.findIndex((ticketComment) => ticketComment.comment.id === commentId);

    if (commentIndex !== -1) {
      delete this.ticket.comments[commentIndex];
      this.ticket.comments = this.ticket.comments.filter((comment) => !!comment);
    } else {
      let replyIndex = -1;
      this.ticket.replies.forEach((reply, mi) => {
        reply.comments.forEach((replyComment, ci) => {
          if (replyComment.comment.id === commentId) {
            replyIndex = mi;
            commentIndex = ci;
          }
        });
      });

      delete this.ticket.replies[replyIndex].comments[commentIndex];
      this.ticket.replies[replyIndex].comments = this.ticket.replies[replyIndex]
        .comments.filter((comment) => !!comment);
    }

    this.$forceUpdate();
  }
}
